import { useState } from "react";
import { Crown, Pencil, UserMinus, Search, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, usd } from "@/lib/api";
import { useFetch, PageHeader, Panel, Table, Empty, Loading, StatusBadge } from "@/components/common";
import { PartnerDialog } from "@/components/admin";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const rule = (t, v) => (t === "flat" ? `${usd(v)}` : `${v}%`);

function PickUser({ open, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const search = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.get(`/admin/users?role=user&search=${encodeURIComponent(q)}`);
      setRows(data.filter((u) => !u.is_youtuber));
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  if (!open) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121722] border-[#242F42] text-white" data-testid="pick-user-dialog">
        <DialogHeader><DialogTitle className="font-display">Select a registered user</DialogTitle></DialogHeader>
        <form onSubmit={search} className="flex gap-2">
          <div className="relative flex-1"><Search size={15} className="absolute left-3 top-3 text-slate-500" />
            <input className="nx-input pl-9" placeholder="Email, name or user ID" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pick-user-search-input" /></div>
          <button className="nx-btn-secondary" data-testid="pick-user-search-btn">Search</button>
        </form>
        <div className="max-h-72 divide-y divide-[#1E2738] overflow-y-auto rounded-lg border border-[#1E2738]">
          {!rows.length && <p className="p-6 text-center text-sm text-slate-500">Search to find users</p>}
          {rows.map((u) => (
            <button key={u.id} onClick={() => onPick(u)} className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-[#1A2232]" data-testid={`pick-user-${u.user_code}`}>
              <div><p className="text-sm text-white">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></div>
              <span className="num text-xs text-[#10B981]">{u.user_code}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminYoutubers() {
  const { data, loading, reload } = useFetch("/admin/youtubers");
  const [edit, setEdit] = useState(null);
  const [pick, setPick] = useState(false);
  const demote = async (u) => {
    if (!window.confirm(`Remove YouTuber Partner status from ${u.name}? Their referral code stays the same.`)) return;
    try {
      await api.delete(`/admin/youtubers/${u.id}`);
      toast.success("Partner status removed");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Admin" title="YouTuber management" subtitle="Promote users to partners, assign unique codes and set independent commission rules."
        actions={<button className="nx-btn-primary" onClick={() => setPick(true)} data-testid="add-youtuber-btn"><Plus size={16} />Promote user</button>} />
      <Panel>
        {loading ? <Loading /> : (
          <Table head={["Partner", "Code", "First-deposit bonus", "Deposit comm.", "Trading comm.", "Referrals", "Earned", "Pending", "Status", ""]} testid="youtubers-table">
            {!data?.length && <Empty cols={10} text="No YouTuber partners yet" />}
            {data?.map((u) => (
              <tr key={u.id} data-testid={`youtuber-row-${u.user_code}`}>
                <td className="nx-td"><p className="flex items-center gap-1.5 text-white"><Crown size={13} className="text-[#10B981]" />{u.name}</p><p className="text-xs text-slate-500">{u.partner?.channel_name || u.email}</p></td>
                <td className="nx-td num text-[#10B981]" data-testid={`youtuber-code-${u.user_code}`}>{u.referral_code}</td>
                <td className="nx-td num">{usd(u.partner?.first_deposit_bonus)}</td>
                <td className="nx-td num">{rule(u.partner?.deposit_type, u.partner?.deposit_value)}</td>
                <td className="nx-td num">{rule(u.partner?.trading_type, u.partner?.trading_value)}</td>
                <td className="nx-td num">{u.referred_count}</td>
                <td className="nx-td num text-[#10B981]">{usd(u.earned_total)}</td>
                <td className="nx-td num text-amber-400">{usd(u.pending_total)}</td>
                <td className="nx-td"><StatusBadge status={u.partner?.active === false ? "suspended" : "active"} /></td>
                <td className="nx-td text-right">
                  <button onClick={() => setEdit(u)} className="mr-3 text-slate-400 hover:text-[#10B981]" data-testid={`edit-youtuber-${u.user_code}`}><Pencil size={15} /></button>
                  <button onClick={() => demote(u)} className="text-slate-400 hover:text-red-400" data-testid={`demote-youtuber-${u.user_code}`}><UserMinus size={15} /></button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
      <PickUser open={pick} onClose={() => setPick(false)} onPick={(u) => { setPick(false); setEdit(u); }} />
      <PartnerDialog target={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); reload(); }} />
    </div>
  );
}
